import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Wishlist = () => {
  const { wishlistProducts, loading, removeFromWishlist, wishlistCount } = useWishlist();
  const { addToCart } = useCart();

  const handleRemove = async (productId) => {
    const success = await removeFromWishlist(productId);
    if (success) {
      toast.success('Removed from wishlist');
    }
  };

  const handleAddToCart = async (product) => {
    const success = await addToCart(product, 0, 1);
    if (success) {
      toast.success('Added to cart!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="wishlist-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12" data-testid="wishlist-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <Heart className="w-8 h-8 text-[#D4AF37]" fill="#D4AF37" />
          <h1 
            className="text-3xl sm:text-4xl font-bold"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="wishlist-title"
          >
            My Wishlist
          </h1>
          {wishlistCount > 0 && (
            <span className="bg-black text-white text-sm px-3 py-1 rounded-full">
              {wishlistCount} {wishlistCount === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>

        {wishlistProducts.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-wishlist">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-700 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-500 mb-6">Save items you love by clicking the heart icon on any product.</p>
            <Link
              to="/shop"
              className="inline-block bg-black text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlistProducts.map((product) => (
              <div 
                key={product.id} 
                className="group bg-white border border-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`wishlist-item-${product.id}`}
              >
                <Link to={`/product/${product.id}`} className="block">
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                
                <div className="p-4">
                  <Link to={`/product/${product.id}`}>
                    <h3 className="font-medium text-gray-900 hover:text-[#D4AF37] transition-colors line-clamp-2 mb-2">
                      {product.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg font-bold">${product.base_price?.toFixed(2)}</span>
                    {product.compare_at_price > product.base_price && (
                      <span className="text-sm text-gray-400 line-through">
                        ${product.compare_at_price?.toFixed(2)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-black text-white py-2 px-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Add to Cart
                    </button>
                    <button
                      onClick={() => handleRemove(product.id)}
                      className="p-2 border border-gray-200 rounded-full hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors"
                      data-testid={`remove-wishlist-${product.id}`}
                      title="Remove from wishlist"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {wishlistProducts.length > 0 && (
          <div className="mt-12 text-center">
            <Link
              to="/shop"
              className="inline-block border-2 border-black text-black px-8 py-3 rounded-full hover:bg-black hover:text-white transition-colors"
              data-testid="browse-more-btn"
            >
              Browse More Products
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
